<template>
  <aside class="aiw2-sidebar" :class="{ 'only-terms': onlyTerms }">
    <!-- Top metric cards (regen credits, POP score, word count, breakdowns). Hidden
         in only-terms mode, where the host (Publish aside) already shows its own. -->
    <template v-if="!onlyTerms">
    <!-- Own-key run: which key/model this article bills -->
    <div v-if="ownKeyProvider" class="side-card api-key-card" :class="'prov-' + ownKeyProvider">
      <div class="akc-ico"><span class="akc-mark">{{ ownKeyProvider === 'anthropic' ? 'CL' : 'AI' }}</span></div>
      <div class="akc-body">
        <div class="akc-title">{{ ownKeyProvider === 'anthropic' ? 'Claude' : 'OpenAI' }} key is on</div>
        <div class="akc-sub">This article bills {{ ownKeyProvider === 'anthropic' ? 'Claude' : 'GPT' }} usage to your {{ ownKeyProvider === 'anthropic' ? 'Anthropic' : 'OpenAI' }} account.</div>
        <span v-if="ownKeyModelLabel" class="akc-model">{{ ownKeyModelLabel }}</span>
      </div>
    </div>
    <!-- Regenerate credits -->
    <div class="side-card">
      <div class="side-card-head" v-click-outside="() => { rcMenuOpen = false; }">
        <h4><span class="rc-icon"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></span> Regenerate credits history</h4>
        <button class="more" aria-label="Regenerate run breakdown" @click="rcMenuOpen = !rcMenuOpen">⋯</button>
        <div class="rc-menu" :class="{ open: rcMenuOpen }">
          <div class="rc-menu-row"><span>Regenerate run - Search engine title and page title</span><b>{{ counts.title }}</b></div>
          <div class="rc-menu-row"><span>Regenerate run - Sub-headings (H2 and H3)</span><b>{{ counts.heads }}</b></div>
          <div class="rc-menu-row"><span>Regenerate run - Main content</span><b>{{ counts.content }}</b></div>
          <div class="rc-menu-row"><span>Regenerate run - Rich content</span><b>{{ counts.rich }}</b></div>
        </div>
      </div>
      <a class="credits-link" @click="showRegenExplain = !showRegenExplain">{{ showRegenExplain ? '▾' : '▸' }} Regeneration Credits Explained</a>
      <p v-if="showRegenExplain" class="credits-explain">Each article includes <strong>5 free regenerate credits</strong> (Title / H1, sub-headings, main content). <template v-if="ownKeyFree">After that, regenerates stay <strong>free</strong> because this article uses your own API key.</template><template v-else>After all 5 are used, each regenerate run costs <strong>1 credit</strong>.</template></p>
      <template v-if="unlimitedRegen">
        <div class="credits-row">
          <span>Regenerate credits</span>
          <span class="val unlimited">Unlimited</span>
        </div>
        <div class="aiw2-help">Regenerates are free on your account.</div>
      </template>
      <template v-else>
        <div class="credits-row">
          <span>Used free regenerate credits</span>
          <span class="val" :class="{ over: paid > 0 }">{{ freeUsed }} / 5{{ paid > 0 ? ' · ' + paid + ' paid' : '' }}</span>
        </div>
        <div class="credits-bar"><div :class="{ over: paid > 0 }" :style="{ width: barPct + '%' }"></div></div>
      </template>
      <!-- SEO-fix run ledger: one row per fix run (time + credits deducted). -->
      <div v-for="(r, i) in fixRuns" :key="'fr' + i" class="credits-row fixrun-row">
        <span>Fix run · {{ r.fixes }} fix{{ r.fixes === 1 ? '' : 'es' }}</span>
        <span class="val"><span class="fr-time">{{ fixRunTime(r.at) }}</span><template v-if="r.credits"> −{{ r.credits }}</template></span>
      </div>
    </div>

    <!-- Current POP score -->
    <div class="side-card">
      <div class="side-card-head"><h4>Current POP score</h4></div>
      <div class="score-big">
        <div class="score-big-num" :class="popColorClass">{{ popScore != null ? popScore : '-' }}<span class="denom">/100</span></div>
        <div class="score-big-lbl">{{ popScore != null ? popScoreLabel : 'Available after draft' }}</div>
      </div>
      <template v-if="showScoreUpdate">
        <div class="score-stale" :class="{ show: scoreStale }">
          <span class="ico">!</span><span>You've changed the content since this score was worked out.</span>
        </div>
        <button class="score-update" type="button" :disabled="!scoreStale || scoring" @click="$emit('update-score')">
          <span v-if="scoring" class="sb-spin"></span>{{ scoring ? 'Re-scoring…' : 'Update optimization score' }}
        </button>
        <div class="score-checked" :class="{ show: !scoreStale && popScore != null }">Last checked just now</div>
      </template>
    </div>

    <!-- Word count -->
    <div class="side-card">
      <div class="side-card-head"><h4>Word count</h4></div>
      <div class="wc-row">
        <div><span class="wc-num">{{ fmt(currentWords) }}</span><span class="wc-total"> / {{ fmt(targetWords) }}</span></div>
        <div class="wc-helper">{{ wcHelper }}</div>
      </div>
      <div class="wc-bar"><div :style="{ width: wcPct + '%' }"></div></div>
    </div>

    <div v-if="pageExperience != null" class="side-card">
      <div class="side-card-head"><h4>Page experience</h4></div>
      <div class="score-big">
        <div class="score-big-num" :class="pageExpClass">{{ pageExperience }}<span class="denom">/100</span></div>
        <div class="score-big-lbl">{{ pageExperience >= 90 ? 'Beautiful, scannable, readable' : 'Good, but needs polish' }}</div>
      </div>
    </div>
    </template>

    <!-- Readiness checklist (Title / Draft / SEO / Publish steps) -->
    <div v-if="showTerms" class="side-section-status">
      <h5>Content readiness
        <v-tooltip top color="transparent">
          <template v-slot:activator="{ on, attrs }">
            <span class="sb-info" v-bind="attrs" v-on="on">info</span>
          </template>
          <div class="white-bg-tooltip small-tooltip">A running checklist of what's still missing before this article is ready. Green means done; anything left tells you exactly what to fix. Nothing here blocks you. It's guidance, not a gate.</div>
        </v-tooltip>
      </h5>
      <div v-for="(it, i) in readinessItems" :key="i" class="sb-row">
        <span class="ico" :class="it.ok ? 'good' : 'warn'">{{ it.ok ? '✓' : '!' }}</span>
        <v-tooltip v-if="it.tip" top color="transparent">
          <template v-slot:activator="{ on, attrs }">
            <span class="lbl lbl-tip" v-bind="attrs" v-on="on">{{ it.label }}</span>
          </template>
          <div class="white-bg-tooltip small-tooltip">{{ it.tip }}</div>
        </v-tooltip>
        <span v-else class="lbl">{{ it.label }}</span>
        <span v-if="it.val" class="val">{{ it.val }}</span>
      </div>
    </div>

    <!-- Report / Content prompts tabs -->
    <template v-if="showTerms">
      <div class="sb-report-tabs">
        <button :class="{ active: tab === 'report' }" @click="tab = 'report'">Report</button>
        <button :class="{ active: tab === 'prompts' }" @click="selectPrompts">Content prompts</button>
      </div>

      <!-- Report tab: term-coverage cards -->
      <div v-show="tab === 'report'" class="sb-tab-panel">
      <div class="sb-view-row">
        <div class="sb-view-toggle">
          <button :class="{ active: view === 'table' }" title="View as table" @click="view = 'table'">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h2v2H4V6zm0 5h2v2H4v-2zm0 5h2v2H4v-2zm4-10h12v2H8V6zm0 5h12v2H8v-2zm0 5h12v2H8v-2z"/></svg>
          </button>
          <button :class="{ active: view === 'tags' }" title="View as tags" @click="view = 'tags'">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/></svg>
          </button>
        </div>
        <button class="sb-sort-btn" title="Sort" @click="sortMenuOpen = !sortMenuOpen">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h12v2H3V6zm0 5h9v2H3v-2zm0 5h6v2H3v-2z"/></svg>
        </button>
        <div v-if="sortMenuOpen" class="sb-sort-menu">
          <button v-for="s in SORTS" :key="s.key" :class="{ active: sortMode === s.key }" @click="sortMode = s.key; sortMenuOpen = false">{{ s.label }}</button>
        </div>
      </div>

      <div v-for="sec in visibleSections" :key="sec" class="sb-term-card" :class="{ collapsed: collapsed[sec], 'show-table': view === 'table' }">
        <div class="sb-term-head" @click="toggleCollapse(sec)">
          <span v-if="sectionIncomplete(sec)" class="chk-warn">
            <svg viewBox="0 0 24 24" fill="none" stroke="#F0A030" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a1.5 1.5 0 0 0 1.29 2.25h17.78A1.5 1.5 0 0 0 22.18 18L13.71 3.86a1.5 1.5 0 0 0-2.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
          </span>
          <span v-else class="chk">✓</span>
          <h4>{{ SECTION_META[sec].label }}</h4>
          <v-tooltip top color="transparent">
            <template v-slot:activator="{ on, attrs }">
              <span class="sb-info" v-bind="attrs" v-on="on" @click.stop>info</span>
            </template>
            <div class="white-bg-tooltip small-tooltip">The words and phrases readers and search engines expect to see in this part of your page. 'Current' is how many times you've used each one; 'Target' is roughly how many times the top-ranking pages use it. Close the gap and this section scores higher.</div>
          </v-tooltip>
          <button class="chev" aria-label="Collapse" @click.stop="toggleCollapse(sec)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>

        <div class="sb-term-body">
          <div class="sb-term-status">
            <label @click.stop="setSectionStatus(sec, 'Completed')"><span class="rd" :class="{ on: sectionMarkedComplete(sec) }"></span>Complete</label>
            <label @click.stop="setSectionStatus(sec, 'InCompleted')"><span class="rd" :class="{ on: !sectionMarkedComplete(sec) }"></span>Incomplete</label>
          </div>
          <span class="sb-term-link" @click="toggleExplain(sec)"><span class="tri" :class="{ open: explainOpen[sec] }">▸</span> {{ SECTION_META[sec].link }}</span>
          <div v-show="explainOpen[sec]" class="sb-term-explain" v-html="SECTION_META[sec].explain"></div>
          <div class="sb-term-actions">
            <button title="Add notes" @click="toggleNotes(sec)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
            </button>
            <button title="Download as text" @click="downloadSection(sec)">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
            </button>
          </div>
          <textarea v-if="notesOpen[sec]" class="sb-term-note" :value="noteDraft[sec]" placeholder="Add a note for this section…"
            @input="noteDraft[sec] = $event.target.value" @blur="saveNote(sec)"></textarea>

          <!-- Tags view -->
          <div class="sb-term-tags">
            <div class="sb-tags-count">( {{ summaryFor(sec).cur }} / {{ summaryFor(sec).min }} - {{ summaryFor(sec).max }} )</div>
            <div class="sb-term-chips">
              <v-tooltip v-for="(r, i) in rowsFor(sec)" :key="'c' + i" top color="transparent">
                <template v-slot:activator="{ on, attrs }">
                  <span class="sb-term-chip" :class="dotClass(r.status)" v-bind="attrs" v-on="on">{{ r.term }} <b>{{ r.current }} / {{ r.min }}-{{ r.max }}</b></span>
                </template>
                <div class="white-bg-tooltip small-tooltip">{{ dotTooltip(r.status) }}</div>
              </v-tooltip>
            </div>
            <div v-if="!rowsFor(sec).length" class="aiw2-help">No specific term targets.</div>
          </div>

          <!-- Table view -->
          <div class="sb-term-tableview">
            <div class="sb-term-counts">
              <div class="col"><div class="big">{{ summaryFor(sec).cur }} terms</div><div class="sub">Current count</div></div>
              <div class="divider"></div>
              <div class="col"><div class="big">{{ summaryFor(sec).min }} - {{ summaryFor(sec).max }} terms</div><div class="sub">Important terms range</div></div>
            </div>
            <table v-if="rowsFor(sec).length" class="sb-term-table">
              <thead><tr><th>Important Term</th><th>Current</th><th>Target</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in rowsFor(sec)" :key="'r' + i">
                  <td><span class="term"><span class="cbx"></span>{{ r.term }}</span></td>
                  <td><span class="cur">{{ r.current }} <v-tooltip top color="transparent">
                    <template v-slot:activator="{ on, attrs }">
                      <span class="dot" :class="dotClass(r.status)" v-bind="attrs" v-on="on"></span>
                    </template>
                    <div class="white-bg-tooltip small-tooltip">{{ dotTooltip(r.status) }}</div>
                  </v-tooltip></span></td>
                  <td>{{ r.min }} - {{ r.max }}<div v-if="r.is_main_keyword" class="must-use">Must Use</div></td>
                </tr>
              </tbody>
            </table>
            <div v-else class="aiw2-help">No specific term targets.</div>
          </div>
        </div>
      </div>
      </div>

      <!-- Content prompts tab -->
      <div v-show="tab === 'prompts'" class="sb-tab-panel">
        <sidebar-content-prompts :page-run="pageRun" :loading="pageRunLoading" />
      </div>
    </template>
  </aside>
</template>

<script>
import { aiWriterTermNotes } from "@/api";
import SidebarContentPrompts from "./SidebarContentPrompts.vue";

const SECTION_META = {
  title: {
    label: "Search Engine Title",
    link: "How to optimize your search engine title",
    explain: "Make sure that your title contains your important terms in the suggested range. You do not need to use every important term. You should select the ones you like and use them to reach the target range for the section.<br/>Use your competitor titles in Content prompts for inspiration.<br/>Your Search Engine Title, the title that search engines see, and your H1, the title that humans see, should be as identical as possible.",
  },
  pageTitle: {
    label: "Page Title",
    link: "How to optimize your page title",
    explain: "Make sure that your page title contains your important terms in the suggested range. You do not need to use every important term. You should select the ones you like and use them to reach the target range for the section.<br/>Use your competitor titles in the Content prompts for inspiration.<br/>Your Search Engine title, the title that search engines see, and your H1, the title that humans see, should be as identical as possible. You should only have one H1, one title visible on your page.",
  },
  subHeadings: {
    label: "Sub-headings",
    link: "How to optimize your sub-headings",
    explain: "No matter how many subheadings/subtitles are on your page makes sure those subheadings/subtitles include the important terms below. You do not have to use every important term. You do not have to get every important term inside its individual target range.<br/>The goal is to use as many of the important terms below as needed so that the Subheadings Section as a whole gets inside the section's target range.<br/>Use the relevant related keywords in Content prompts for inspiration on your subtitles. <b>If you see competitor brand names in this list we highly recommend that you re-run and remove them.</b>",
  },
  p: {
    label: "Main Content",
    link: "How to optimize your main content",
    explain: "Use the suggested terms below to optimize the main content on your page. You do not have to use every important term. You do not have to get every important term inside its individual target range.<br/>The goal is to use as many of the important terms below as needed so that the Main Content Section as a whole gets inside the section's target range.<br/>A good approach is to provide information that answers the questions your readers have about your target keyword. It's often good to write your main content first and then edit for the important terms. Use the related questions in Content prompts for inspiration on the questions your readers may need answered. <b>If you see competitor brand names in this list we highly recommend that you re-run and remove them.</b>",
  },
};
// Fraction of the section max that becomes the box total's min — matches
// popalchemy.TARGET_MIN_PERCENTAGE (popalchemy.py:2883-2888), which the content
// editor's get_total_data uses. (Frontend constants.js has p:0.65 — wrong.)
const TERM_MIN_PCT = { title: 0.5, pageTitle: 0.5, subHeadings: 0.55, p: 0.6 };
const SORTS = [
  { key: "important", label: "Most Important" },
  { key: "deficit", label: "Deficit" },
  { key: "alphabetic", label: "Alphabetic" },
  { key: "status", label: "Status" },
];

export default {
  name: "AiwV2ReportSidebar",
  components: { SidebarContentPrompts },
  props: {
    session: { type: Object, default: null },
    setup: { type: Object, default: null },
    step: { type: String, default: "" },
    planning: { type: Object, default: null },
    seoQuality: { type: Object, default: null },
    keyword: { type: String, default: "" },
    // Draft-step "Update optimization score" control: enabled once the user has
    // edited content since the last score (stale); shows a spinner while re-scoring.
    scoreStale: { type: Boolean, default: false },
    scoring: { type: Boolean, default: false },
    // When embedded inside a host that already shows the metric cards (Publish
    // aside), render only the readiness + term-coverage boxes.
    onlyTerms: { type: Boolean, default: false },
    // Title step only: an uncommitted candidate title `{ seoTitle, pageH1 }` to
    // preview term coverage against before the user commits the selection.
    liveCandidate: { type: Object, default: null },
  },
  data() {
    return {
      SECTION_META, SORTS,
      tab: "report",
      pageRun: null,
      pageRunLoading: false,
      view: "table",
      sortMode: "important",
      sortMenuOpen: false,
      showRegenExplain: false,
      rcMenuOpen: false,
      // Term-coverage cards start minimized (collapsed) by default.
      collapsed: { title: true, pageTitle: true, subHeadings: true, p: true },
      // Per-section "How to optimize …" explanation open state.
      explainOpen: {},
      // Optimistic per-section Complete/Incomplete overrides (merged over session).
      statusOverride: {},
      notesOpen: {},
      noteDraft: { ...(((this.planning || {}).term_notes) || {}) },
    };
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    aiRunId() {
      return this.session && this.session.aiRunId;
    },
    sq() {
      return this.seoQuality || (this.session && this.session.seoQuality) || null;
    },
    pageExperience() {
      return (this.sq && this.sq.scores && this.sq.scores.page_experience_score) != null
        ? this.sq.scores.page_experience_score
        : null;
    },
    pageExpClass() {
      const v = this.pageExperience;
      if (v == null) return "";
      if (v >= 90) return "tone-green";
      return v >= 70 ? "tone-amber" : "tone-orange";
    },
    coverage() {
      const base = (this.session && this.session.termCoverage) || {};
      // Title step: preview coverage for an uncommitted candidate title so the
      // user can evaluate it before committing. rowsFor/summaryFor already prefer
      // coverage[section] when present, so this overlay needs no template change.
      const c = this.liveCandidate;
      if (this.step === "title" && c && (c.seoTitle || c.pageH1)) {
        return {
          ...base,
          title: this.liveRows("title", c.seoTitle),
          pageTitle: this.liveRows("pageTitle", c.pageH1),
        };
      }
      return base;
    },
    contentGenerated() {
      return !!(this.session && this.session.blocksSummary && this.session.blocksSummary.count > 0);
    },
    visibleSections() {
      if (this.step === "title") return ["title", "pageTitle"];
      // Draft and every later step (SEO Quality / Publish) show the content term
      // boxes so the user keeps their coverage read-out through to publishing.
      if (["draft", "seo_quality", "publish"].includes(this.step)) {
        // Main Content ('p') = body/paragraph terms — only meaningful once the
        // draft has been written, so show it under Sub-headings after generation.
        const secs = ["title", "pageTitle", "subHeadings"];
        if (this.contentGenerated) secs.push("p");
        return secs;
      }
      return [];
    },
    showTerms() {
      return this.visibleSections.length > 0;
    },
    selectedTitle() {
      return (this.session && this.session.title && this.session.title.selected) || {};
    },
    // The title the readiness checklist should judge: on the Title step use the
    // live (uncommitted) candidate so clicking a suggestion updates "Length good"
    // etc. immediately; elsewhere fall back to the committed selection.
    activeTitle() {
      const c = this.liveCandidate;
      if (this.step === "title" && c && (c.seoTitle || c.pageH1)) {
        return { seo_title: c.seoTitle || "", page_h1: c.pageH1 || "" };
      }
      return this.selectedTitle;
    },
    // Per-section manual Complete/Incomplete status: the persisted Page status
    // (session.contentBriefStatus) with any optimistic click override on top.
    sectionStatusMap() {
      return { ...((this.session && this.session.contentBriefStatus) || {}), ...this.statusOverride };
    },
    bannedWords() {
      const ew = (this.setup && this.setup.advanced && this.setup.advanced.excluded_words) || {};
      return [].concat(ew.hard || [], ew.soft || []);
    },
    readinessItems() {
      const seo = this.activeTitle.seo_title || "";
      const h1 = this.activeTitle.page_h1 || "";
      const kw = (this.keyword || "").toLowerCase();
      // Title / H1 readiness mirrors the term boxes' warn icon (sectionIncomplete:
      // coverage below min OR main keyword unused) so the checklist can never show
      // green while the box below says Incomplete. Without a term plan yet, fall
      // back to plain keyword-presence so the rows don't fail for lack of data.
      const termRow = (label, section, text) => {
        const s = this.summaryFor(section);
        if (!s.count) {
          return { label, ok: !!(text && kw && text.toLowerCase().indexOf(kw) !== -1) };
        }
        const mk = this.mainKwRow(section);
        return {
          label,
          ok: !this.sectionIncomplete(section),
          val: s.cur + " / " + s.min + "-" + s.max,
          tip: mk && !(mk.current > 0) ? "Add your primary keyword" : "",
        };
      };
      const items = [
        termRow("SEO title terms", "title", seo),
        termRow("H1 terms", "pageTitle", h1),
        { label: "Title length good", ok: seo.length >= 40 && seo.length <= 65, val: seo ? seo.length + " chars" : "" },
      ];
      // No banned words on every step — flag any that appear in the SEO title OR the body.
      // When present, show "Some banned words" (yellow ! icon) with the words in a tooltip.
      const titleHits = this.bannedWords.filter((w) => w && seo.toLowerCase().indexOf(String(w).toLowerCase()) !== -1);
      const bodyHits = ((this.session || {}).bannedWordsFound) || [];
      const bannedHits = Array.from(new Set([...titleHits, ...bodyHits]));
      items.push({
        label: bannedHits.length ? "Some banned words" : "No banned words",
        ok: bannedHits.length === 0,
        tip: bannedHits.length ? bannedHits.join(", ") : "",
      });
      if (this.step !== "title") {
        // Sub-heading terms from the Draft step onward (subheadings exist by then).
        const s = this.summaryFor("subHeadings");
        items.push({ label: "Sub-heading terms", ok: s.cur >= s.min, val: s.cur + " / " + s.min + "-" + s.max });
        // Main content terms once the draft has actually been generated.
        if (((this.session || {}).blocksSummary || {}).generated) {
          const p = this.summaryFor("p");
          items.push({ label: "Main content terms", ok: p.cur >= p.min, val: p.cur + " / " + p.min + "-" + p.max });
        }
        // Authoritative web sources, when the feature is enabled for this run.
        const web = (this.session || {}).webSources || {};
        if (web.enabled) {
          items.push({
            label: "Sources cited",
            ok: web.status === "complete" && web.count > 0,
            val: web.count ? String(web.count)
              : (web.status === "running" ? "researching…" : "none found"),
          });
        }
      }
      return items;
    },
    unlimitedRegen() {
      return !!(this.session && this.session.credits && this.session.credits.unlimitedRegenerate);
    },
    // Own-key runs consume the free pool first, then regenerates stay free.
    ownKeyFree() {
      return !!(this.session && this.session.credits && this.session.credits.ownKeyFree);
    },
    // 'openai' | 'anthropic' for own-key runs (null on POP-credit runs).
    ownKeyProvider() {
      return (this.session && this.session.credits && this.session.credits.ownKeyProvider) || null;
    },
    ownKeyModelLabel() {
      return (this.session && this.session.credits && this.session.credits.ownKeyModelLabel) || '';
    },
    regenIncluded() {
      return (this.session && this.session.credits && this.session.credits.regenerateIncluded) != null
        ? this.session.credits.regenerateIncluded : 5;
    },
    regenRemaining() {
      return (this.session && this.session.credits && this.session.credits.regenerateRemaining) != null
        ? this.session.credits.regenerateRemaining : this.regenIncluded;
    },
    regenUsed() {
      return Math.max(0, this.regenIncluded - this.regenRemaining);
    },
    regenPct() {
      return this.regenIncluded ? (this.regenUsed / this.regenIncluded) * 100 : 0;
    },
    // Per-category TOTAL regenerate-run counts (backend planning_dict.regenerate_counts).
    counts() {
      const c = (this.session && this.session.credits && this.session.credits.regenerateCounts) || {};
      return { title: c.title || 0, heads: c.heads || 0, content: c.content || 0, rich: c.rich || 0 };
    },
    paid() {
      return (this.session && this.session.credits && this.session.credits.regeneratePaid) || 0;
    },
    // SEO-fix run ledger entries ({at, fixes, credits}), newest last.
    fixRuns() {
      return (this.session && this.session.credits && this.session.credits.fixRuns) || [];
    },
    freeUsed() {
      return Math.min(5, this.regenUsed);
    },
    barPct() {
      return Math.min(100, ((this.freeUsed + this.paid) / 5) * 100);
    },
    popScore() {
      return (this.session && this.session.scores && this.session.scores.popScore) != null
        ? Math.round(this.session.scores.popScore) : null;
    },
    // The "Update optimization score" control shows only on the Draft step once a
    // draft has been generated (there's a real score to refresh).
    showScoreUpdate() {
      return this.step === "draft" && !!(((this.session || {}).blocksSummary || {}).generated);
    },
    popColorClass() {
      const v = this.popScore;
      if (v == null) return "";
      if (v >= 85) return "tone-green";
      return v >= 60 ? "tone-amber" : "tone-red";
    },
    popScoreLabel() {
      return this.sq && this.sq.analyzed ? "After review" : "After draft";
    },
    currentWords() {
      return (this.session && this.session.scores && this.session.scores.wordCount) ||
        (this.session && this.session.blocksSummary && this.session.blocksSummary.wordCount) || 0;
    },
    targetWords() {
      return (this.setup && this.setup.word_count) ||
        (this.session && this.session.scores && this.session.scores.targetWordCount) || 0;
    },
    wordsRemaining() {
      return Math.max(0, this.targetWords - this.currentWords);
    },
    wcPct() {
      if (!this.targetWords) return 0;
      return Math.min(100, (this.currentWords / this.targetWords) * 100);
    },
    wcHelper() {
      if (!this.currentWords) return "Will populate after draft";
      if (!this.targetWords) return "";
      if (this.wordsRemaining > 0) return this.fmt(this.wordsRemaining) + " words below target";
      if (this.currentWords > this.targetWords) return this.fmt(this.currentWords - this.targetWords) + " words above target ✓";
      return "Target reached ✓";
    },
  },
  watch: {
    planning: {
      deep: true,
      handler(p) {
        // Keep local note drafts in sync when the server-persisted notes change.
        const notes = (p && p.term_notes) || {};
        Object.keys(notes).forEach((k) => {
          if (this.noteDraft[k] === undefined) this.$set(this.noteDraft, k, notes[k]);
        });
      },
    },
  },
  methods: {
    fmt(n) {
      return (n || 0).toLocaleString("en-US");
    },
    // "19:27"-style local time for a fix-run ledger entry (backend sends UTC iso
    // without a zone suffix — append Z so it converts to the viewer's local time).
    fixRunTime(at) {
      if (!at) return "";
      try {
        const d = new Date(/Z|[+-]\d\d:?\d\d$/.test(at) ? at : at + "Z");
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
      } catch (e) {
        return "";
      }
    },
    // Content-prompts data lives on the report's PageRun; fetch it lazily the
    // first time the user opens the tab (reusing the store action the editor
    // already uses to load the report).
    async selectPrompts() {
      this.tab = "prompts";
      if (this.pageRun || this.pageRunLoading || !this.session || !this.session.pageRunId) return;
      this.pageRunLoading = true;
      try {
        const res = await this.$store.dispatch("fetchPageRunBySecretKeyOrPageRunId", { pageRunId: this.session.pageRunId });
        this.pageRun = (res && res.data && res.data.pageRun) || null;
      } catch (e) {
        /* leave null — the panel shows its empty state */
      } finally {
        this.pageRunLoading = false;
      }
    },
    dotClass(status) {
      return status === "green" ? "g" : status === "purple" ? "p" : "o";
    },
    dotTooltip(status) {
      if (status === "purple") {
        return "While you are over the target range for this term, we do not recommend reducing this term until you have increased the use of other terms first";
      }
      if (status === "green") {
        return "You are in the target range for this term. We recommend reaching at least the mid-point of all target ranges for best results.";
      }
      // orange / red: below the target range (same guidance for both)
      return "You are below the target range for this term. We recommend increasing the use of this term to get into the target range.";
    },
    // Live term coverage for a candidate title (title step preview). Term list
    // reuses the committed coverage rows when present, else the term plan; each
    // term's `current`/`status` is recomputed against the candidate text.
    liveRows(section, rawText) {
      const cov = ((this.session && this.session.termCoverage) || {})[section];
      const src = cov && cov.length ? cov : (((this.planning || {}).term_plan || {})[section] || []);
      const text = String(rawText || "").replace(/\s+/g, " ").trim();
      return src.map((t) => {
        const min = t.min != null ? t.min : (t.target_min != null ? t.target_min : (t.count || 0));
        const max = t.max != null ? t.max : (t.target_max != null ? t.target_max : (t.count || 0));
        const current = this.countTerm(t.term, text);
        const status = current > max ? "purple" : (current < min || (current === 0 && max > 0)) ? "orange" : "green";
        return { term: t.term, current, min, max, is_main_keyword: t.is_main_keyword, status };
      });
    },
    // Case-insensitive whole-phrase count, mirroring the backend coverage matcher
    // (make_bounded_word + count_word_count): normalize the term, escape it, and
    // match \bterm\b. No stemming/plural variants (those are highlight-only).
    countTerm(phrase, text) {
      const norm = String(phrase || "").toLowerCase()
        .replace(/&nbsp;/g, " ").replace(/&rsquo;/g, "_").replace(/[.,:?]/g, " ").replace(/’/g, "'").trim();
      if (!norm || !text) return 0;
      const esc = norm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return (String(text).match(new RegExp("\\b" + esc + "\\b", "gi")) || []).length;
    },
    mainKwRow(section) {
      return (this.coverage[section] || []).find((r) => r.is_main_keyword) || null;
    },
    rowsFor(section) {
      const cov = this.coverage[section];
      let rows;
      if (cov && cov.length) {
        rows = cov.slice();
      } else {
        const plan = ((this.planning || {}).term_plan || {})[section] || [];
        rows = plan.map((t) => {
          const min = t.target_min != null ? t.target_min : (t.count || 0);
          const max = t.target_max != null ? t.target_max : (t.count || 0);
          return { term: t.term, current: 0, min, max, is_main_keyword: t.is_main_keyword, status: max > 0 ? "orange" : "green" };
        });
      }
      return this.sortRows(rows);
    },
    sortRows(rows) {
      const r = rows.slice();
      if (this.sortMode === "alphabetic") r.sort((a, b) => (a.term || "").localeCompare(b.term || ""));
      else if (this.sortMode === "deficit") r.sort((a, b) => (b.min - b.current) - (a.min - a.current));
      else if (this.sortMode === "status") {
        const o = { orange: 0, purple: 1, green: 2 };
        r.sort((a, b) => o[a.status] - o[b.status]);
      } else r.sort((a, b) => (b.is_main_keyword ? 1 : 0) - (a.is_main_keyword ? 1 : 0));
      return r;
    },
    summaryFor(section) {
      const rows = this.coverage[section] && this.coverage[section].length
        ? this.coverage[section]
        : ((this.planning || {}).term_plan || {})[section] || [];
      const termMax = (r) => (r.max != null ? r.max : (r.target_max != null ? r.target_max : (r.count || 0)));
      // Match the content editor (popalchemy.get_total_data): cap each term's
      // current at its max, sum the maxes, and derive min as a % of the section
      // max total — so the box total equals the content editor's section total.
      const max = rows.reduce((s, r) => s + termMax(r), 0);
      const cur = rows.reduce((s, r) => s + Math.min(r.current || 0, termMax(r)), 0);
      const min = Math.round(max * (TERM_MIN_PCT[section] || 0.5));
      return { cur, min, max, count: rows.length };
    },
    sectionIncomplete(section) {
      // Auto readiness for the card-head warn/✓ icon (term coverage) — independent
      // of the manual Complete/Incomplete radios (sectionMarkedComplete).
      const s = this.summaryFor(section);
      if (s.cur < s.min) return true;
      const mk = this.mainKwRow(section);
      return !!(mk && mk.status === "orange");
    },
    sectionMarkedComplete(section) {
      // Manual, user-set status persisted on the shared Page (Content Brief radios).
      return this.sectionStatusMap[section] === "Completed";
    },
    setSectionStatus(section, status) {
      if (this.sectionStatusMap[section] === status) return;
      const prev = this.sectionStatusMap[section];
      this.$set(this.statusOverride, section, status); // optimistic
      const pageId = this.session && this.session.pageId;
      if (!pageId) return;
      this.$store.dispatch("updateContentBriefStatus", {
        willUseSecretKey: false, secretKey: "", pageId, sectionName: section, status,
      }).catch(() => {
        if (prev) this.$set(this.statusOverride, section, prev);
        else this.$delete(this.statusOverride, section);
      });
    },
    toggleCollapse(section) {
      this.$set(this.collapsed, section, !this.collapsed[section]);
    },
    toggleExplain(section) {
      this.$set(this.explainOpen, section, !this.explainOpen[section]);
    },
    toggleNotes(section) {
      if (this.noteDraft[section] === undefined) {
        this.$set(this.noteDraft, section, (((this.planning || {}).term_notes) || {})[section] || "");
      }
      this.$set(this.notesOpen, section, !this.notesOpen[section]);
    },
    async saveNote(section) {
      if (!this.aiRunId) return;
      try {
        await aiWriterTermNotes(this.token, this.aiRunId, section, this.noteDraft[section] || "");
      } catch (e) { /* ignore */ }
    },
    downloadSection(section) {
      const rows = this.rowsFor(section);
      let text = SECTION_META[section].label + " terms\n\n";
      rows.forEach((r) => {
        text += r.term + "\t" + r.current + " / " + r.min + "-" + r.max + (r.is_main_keyword ? "  (Must Use)" : "") + "\n";
      });
      const blob = new Blob([text], { type: "text/plain" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = section + "-terms.txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>

<style scoped>
.aiw2-sidebar {
  display: flex; flex-direction: column; gap: 10px;
  width: 300px; flex-shrink: 0;
  font-family: "Mulish", system-ui, sans-serif;
}
/* Embedded (Publish aside) — fill the host column instead of a fixed 300px. */
.aiw2-sidebar.only-terms { width: 100%; }
.side-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e4e4e4);
  border-radius: var(--r-lg, 10px);
  padding: 16px 18px;
}
.side-card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; position: relative; }
/* Own-key "key is on" card — icon rail + body, tinted per provider (mockup .api-key-card). */
.api-key-card { display: flex; align-items: stretch; gap: 0; padding: 0; overflow: hidden; }
.akc-ico { flex: none; width: 58px; display: flex; align-items: center; justify-content: center; }
.akc-body { padding: 12px 14px; }
.akc-title { font-size: 14px; font-weight: 800; color: var(--text, #052e33); }
.akc-sub { font-size: 12px; color: var(--text-2, #47606a); margin-top: 2px; }
/* provider identity: the two keys must never look alike at a glance */
.akc-mark {
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800; letter-spacing: .02em; color: #fff;
}
.api-key-card.prov-openai { border-color: #10a37f; background: #F1FBF7; }
.api-key-card.prov-openai .akc-ico { background: #DCF5EC; }
.api-key-card.prov-openai .akc-mark { background: #10a37f; }
.api-key-card.prov-anthropic { border-color: #D97757; background: #FDF5F1; }
.api-key-card.prov-anthropic .akc-ico { background: #F7E3D9; }
.api-key-card.prov-anthropic .akc-mark { background: #D97757; }
.akc-model {
  display: inline-block; margin-top: 5px; font-size: 11px; font-weight: 700;
  color: var(--text-2, #47606a); background: #fff;
  border: 1px solid var(--border, #e4e4e4); border-radius: 999px; padding: 2px 8px;
}
.side-card-head h4 { flex: 1; margin: 0; font-size: 14px; font-weight: 700; color: var(--text, #052e33); display: flex; align-items: center; gap: 6px; }
.side-card-head .rc-icon { display: inline-flex; align-items: center; color: var(--text-3, #6b7b7e); }
.more { background: var(--surface-2, #f7f7f7); border: none; width: 26px; height: 26px; border-radius: 50%; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 14px; line-height: 1; display: flex; align-items: center; justify-content: center; }
.more:hover { background: var(--border, #e4e4e4); color: var(--text-2, #014751); }
.rc-menu { display: none; position: absolute; top: 30px; right: 0; width: 296px; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); box-shadow: 0 18px 44px rgba(15,20,25,.18); padding: 6px; z-index: 60; }
.rc-menu.open { display: block; }
.rc-menu-row { display: flex; gap: 12px; align-items: baseline; padding: 9px 10px; border-radius: 6px; font-size: 12.5px; color: var(--text-2, #014751); }
.rc-menu-row:hover { background: var(--surface-2, #f7f7f7); }
.rc-menu-row span { flex: 1; font-size: 12.5px; }
.rc-menu-row b { font-weight: 800; color: var(--text, #052e33); font-variant-numeric: tabular-nums; }
.credits-row .val.over { color: var(--pop-orange-deep, #b23d00); }
.credits-link { color: var(--pop-blue, #316bff); text-decoration: underline; font-size: 12.5px; display: inline-flex; align-items: center; gap: 4px; margin-bottom: 8px; cursor: pointer; }
.credits-link:hover { text-decoration: underline; }
.credits-explain { font-size: 11.5px; color: var(--text-3, #6b7b7e); line-height: 1.5; margin: 0 0 8px; }

/* Regenerate credits */
.credits-row { display: flex; justify-content: space-between; align-items: center; font-size: 12.5px; margin-bottom: 6px; color: var(--text-2, #014751); }
/* SEO-fix run ledger rows (under the used-free counter) */
.fixrun-row { margin-top: 8px; margin-bottom: 0; font-size: 12px; }
.fixrun-row .fr-time { color: var(--text-3, #6b7b7e); font-weight: 500; margin-right: 6px; }
.credits-row .val { font-weight: 700; color: var(--text, #052e33); }
.credits-row .val.unlimited { color: var(--pop-green, #25a97a); }
.credits-bar { height: 4px; background: var(--pop-orange-soft, #ffeddd); border-radius: 999px; overflow: hidden; }
.credits-bar > div { height: 100%; background: var(--pop-orange, #ff5700); border-radius: 999px; }

/* Score (big) */
.score-big { text-align: center; padding: 6px 0 4px; }
/* Color modifiers are prefixed `tone-` to avoid Vuetify's global `.green` /
   `.amber` / `.orange` / `.red` color utilities, which force a background-color. */
.score-big-num { font-size: 44px; font-weight: 700; color: var(--text-muted, #9aa6a8); line-height: 1; }
.score-big-num.tone-green { color: var(--pop-green, #25a97a); }
.score-big-num.tone-orange { color: var(--pop-orange, #ff5700); }
.score-big-num.tone-amber { color: var(--pop-amber, #aa993b); }
.score-big-num.tone-red { color: var(--pop-red, #e5484d); }
.score-big-num .denom { font-size: 18px; color: var(--text-3, #6b7b7e); font-weight: 500; }
.score-big-lbl { font-size: 11px; color: var(--text-3, #6b7b7e); margin-top: 4px; font-weight: 500; }

/* Update optimization score (Draft step) */
.score-stale { display: none; gap: 8px; align-items: flex-start; margin-top: 12px; padding: 9px 11px; border-radius: var(--r-lg, 10px); background: var(--surface-2, #f7f7f7); border: 1px solid var(--border-strong, #ccc); font-size: 12px; line-height: 1.45; color: var(--text-2, #014751); }
.score-stale.show { display: flex; }
.score-stale .ico { flex: none; width: 15px; height: 15px; border-radius: 50%; background: var(--pop-ink-2, #013840); color: #fff; font-size: 10px; font-weight: 800; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.score-update { width: 100%; justify-content: center; margin-top: 12px; display: inline-flex; align-items: center; gap: 7px; padding: 9px 12px; font-family: inherit; font-size: 13px; font-weight: 600; border-radius: 8px; cursor: pointer; background: #fff; border: 1.5px solid var(--pop-orange, #ff5700); color: var(--pop-orange-deep, #b23d00); transition: all .15s; }
.score-update:hover:not(:disabled) { background: var(--pop-orange-soft, #ffeddd); }
.score-update:disabled { opacity: .5; cursor: default; }
.score-checked { display: none; margin-top: 8px; font-size: 11px; color: var(--text-3, #6b7b7e); text-align: center; }
.score-checked.show { display: block; }
.sb-spin { width: 12px; height: 12px; border: 2px solid var(--pop-orange-tint, #ffd9c2); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: sb-spin .7s linear infinite; }
@keyframes sb-spin { to { transform: rotate(360deg); } }

/* Word count */
.wc-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.wc-num { font-size: 22px; font-weight: 700; color: var(--text, #052e33); }
.wc-total { color: var(--text-3, #6b7b7e); font-size: 14px; font-weight: 500; }
.wc-helper { font-size: 11.5px; color: var(--text-3, #6b7b7e); margin-left: auto; text-align: right; line-height: 1.4; }
.wc-bar { height: 4px; background: #fce4d7; border-radius: 999px; overflow: hidden; }
.wc-bar > div { height: 100%; background: linear-gradient(90deg, var(--pop-orange, #ff5700) 0%, #ff9966 100%); border-radius: 999px; transition: width .6s; }

/* Rows (breakdown + readiness share .sb-row) */
.sb-row { display: flex; align-items: center; gap: 10px; padding: 5px 0; font-size: 12.5px; color: var(--text-2, #014751); }
.sb-row .lbl { flex: 1; font-weight: 500; }
.sb-row .val { font-size: 12px; color: var(--text-3, #6b7b7e); font-weight: 500; text-align: right; }
.sb-row .ico { width: 14px; height: 14px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; flex-shrink: 0; color: #fff; }
.sb-row .ico.good { background: var(--pop-green, #25a97a); }
.sb-row .ico.warn { background: var(--pop-amber, #aa993b); }
.sb-row .lbl-tip { text-decoration: underline dotted; text-underline-offset: 2px; cursor: default; }

/* Readiness */
.side-section-status { background: var(--surface, #fff); border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 12px 16px; }
.side-section-status h5 { margin: 0 0 6px; font-size: 13.5px; font-weight: 700; color: var(--text, #052e33); display: flex; align-items: center; gap: 6px; }
.sb-info { width: 16px; height: 16px; display: inline-block; flex-shrink: 0; vertical-align: middle; cursor: pointer; user-select: none; font-size: 0; line-height: 0; background-color: #014751; -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; }

/* Report / Content prompts tab bar (design handoff) */
.sb-report-tabs { display: flex; gap: 0; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: 8px; padding: 3px; }
.sb-report-tabs button { flex: 1; font-family: inherit; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; padding: 8px 6px; border: none; border-radius: 6px; background: transparent; color: var(--text-3, #6b7b7e); cursor: pointer; }
.sb-report-tabs button.active { background: #fff; color: var(--text, #052e33); box-shadow: 0 1px 2px rgba(0,0,0,.08); }
/* Each tab's content keeps the sidebar's vertical rhythm. */
.sb-tab-panel { display: flex; flex-direction: column; gap: 10px; }

/* Term-card view toolbar */
.sb-view-row { display: flex; align-items: center; gap: 8px; position: relative; }
.sb-view-toggle { display: inline-flex; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: 7px; padding: 2px; }
.sb-view-toggle button { border: none; background: transparent; padding: 4px 8px; cursor: pointer; color: var(--text-3, #6b7b7e); border-radius: 5px; display: inline-flex; }
.sb-view-toggle button.active { background: #fff; color: var(--text-2, #014751); box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.sb-view-toggle svg { width: 15px; height: 15px; display: block; }
.sb-sort-btn { border: 1px solid var(--border, #e4e4e4); background: #fff; border-radius: 7px; padding: 5px 7px; cursor: pointer; color: var(--text-3, #6b7b7e); display: inline-flex; }
.sb-sort-btn svg { width: 15px; height: 15px; }
.sb-sort-menu { position: absolute; top: 100%; left: 0; margin-top: 4px; z-index: 20; background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 8px; box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08)); padding: 4px; display: flex; flex-direction: column; min-width: 140px; }
.sb-sort-menu button { text-align: left; border: none; background: transparent; padding: 7px 10px; font-size: 12.5px; color: var(--text-2, #014751); border-radius: 6px; cursor: pointer; }
.sb-sort-menu button:hover { background: var(--surface-2, #f7f7f7); }
.sb-sort-menu button.active { color: var(--pop-orange, #ff5700); font-weight: 700; }

/* Term card */
.sb-term-card { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 14px; }
.sb-term-head { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
.sb-term-head h4 { font-family: inherit; font-size: 14px; font-weight: 800; color: var(--text, #052e33); margin: 0; line-height: 1; }
.sb-term-head .chk { width: 14px; height: 14px; border-radius: 50%; background: var(--pop-green, #25a97a); color: #fff; font-size: 9px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sb-term-head .chk-warn { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; line-height: 0; }
.sb-term-head .chk-warn svg { width: 16px; height: 16px; }
.sb-term-head .chev { margin-left: auto; flex-shrink: 0; width: 24px; height: 24px; border: none; background: transparent; cursor: pointer; color: var(--text-3, #6b7b7e); display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; transition: background .12s, transform .2s; }
.sb-term-head .chev svg { width: 16px; height: 16px; display: block; }
.sb-term-head:hover .chev { background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); }
.sb-term-card.collapsed .chev { transform: rotate(-90deg); }
.sb-term-body { overflow: hidden; }
.sb-term-card.collapsed .sb-term-body { display: none; }
.sb-term-status { display: flex; gap: 16px; margin: 10px 0 8px; }
.sb-term-status label { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--text-2, #014751); cursor: pointer; }
.sb-term-status .rd { width: 15px; height: 15px; border-radius: 50%; border: 1.5px solid var(--border-strong, #ccc); display: inline-flex; align-items: center; justify-content: center; }
.sb-term-status .rd.on { border-color: var(--pop-blue, #316bff); }
.sb-term-status .rd.on::after { content: ""; width: 8px; height: 8px; border-radius: 50%; background: var(--pop-blue, #316bff); }
.sb-term-link { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-2, #014751); margin-bottom: 10px; cursor: pointer; user-select: none; }
.sb-term-link:hover { text-decoration: underline; }
.sb-term-link .tri { color: var(--text-3, #6b7b7e); font-size: 9px; transition: transform .15s; }
.sb-term-link .tri.open { transform: rotate(90deg); }
.sb-term-explain { font-size: 12px; font-style: italic; color: var(--text-3, #6b7b7e); line-height: 1.5; margin: -2px 0 12px; }
.sb-term-explain b { font-weight: 700; font-style: normal; color: var(--text-2, #014751); }
.sb-term-actions { display: flex; justify-content: flex-end; gap: 6px; margin-bottom: 10px; }
.sb-term-actions button { width: 30px; height: 30px; border: 1px solid var(--border, #e4e4e4); border-radius: 7px; background: #fff; color: var(--text-2, #014751); cursor: pointer; display: flex; align-items: center; justify-content: center; }
.sb-term-actions svg { width: 16px; height: 16px; }
.sb-term-note { width: 100%; box-sizing: border-box; resize: vertical; min-height: 56px; font-family: inherit; font-size: 12.5px; color: var(--text, #052e33); border: 1px solid var(--border, #e4e4e4); border-radius: 8px; padding: 9px 11px; margin: 0 0 12px; background: #fff; }
.sb-term-note:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.sb-term-counts { display: flex; gap: 14px; padding: 4px 0 12px; }
.sb-term-counts .col { flex: 1; }
.sb-term-counts .big { font-size: 16px; font-weight: 800; color: var(--text, #052e33); }
.sb-term-counts .sub { font-size: 11px; color: var(--text-3, #6b7b7e); margin-top: 1px; }
.sb-term-counts .divider { width: 1px; background: var(--border, #e4e4e4); }
table.sb-term-table { width: 100%; border-collapse: collapse; border: 1px solid var(--border, #e4e4e4); border-radius: 6px; overflow: hidden; }
.sb-term-table th { text-align: left; font-size: 10.5px; font-weight: 700; color: var(--text-2, #014751); background: var(--surface-2, #f7f7f7); padding: 8px 10px; border-bottom: 1px solid var(--border, #e4e4e4); }
.sb-term-table td { padding: 9px 10px; border-bottom: 1px solid var(--border, #e4e4e4); font-size: 12px; color: var(--text-2, #014751); vertical-align: top; }
.sb-term-table tr:last-child td { border-bottom: none; }
.sb-term-table .term { display: flex; align-items: flex-start; gap: 7px; }
.sb-term-table .term .cbx { width: 14px; height: 14px; border: 1.5px solid var(--border-strong, #ccc); border-radius: 3px; flex-shrink: 0; margin-top: 1px; }
.sb-term-table .cur { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; }
.sb-term-table .dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.dot.g, .sb-term-chip.g { color: var(--pop-green, #25a97a); }
.sb-term-table .dot.g { background: var(--pop-green, #25a97a); }
.sb-term-table .dot.o { background: #f0a030; }
.sb-term-table .dot.p { background: #9b6bd8; }
.must-use { white-space: nowrap; display: inline-block; margin-top: 4px; background: #ff3939; color: #fff; font-size: 9.5px; font-weight: 700; padding: 2px 7px; border-radius: 4px; letter-spacing: .02em; }

/* Tags / chips */
.sb-tags-count { font-size: 13px; font-weight: 700; color: var(--text-2, #014751); margin: 2px 0 10px; }
.sb-term-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.sb-term-chip { border: 1px solid; border-radius: 6px; padding: 8px 11px; font-size: 12px; font-weight: 600; color: var(--text-2, #014751); }
.sb-term-chip b { font-weight: 800; }
.sb-term-chip.g { border-color: var(--pop-green, #25a97a); }
.sb-term-chip.o { border-color: #f0a030; }
.sb-term-chip.p { border-color: #9b6bd8; }

/* View switching */
.sb-term-tableview { display: none; }
.sb-term-card.show-table .sb-term-tableview { display: block; }
.sb-term-card.show-table .sb-term-tags { display: none; }

.aiw2-help { color: var(--text-3, #6b7b7e); font-size: 12px; }
</style>
